import { gql } from '@apollo/client'
import { SortingState } from '@tanstack/react-table'

import { AdminOrganization } from './adminOrganizationsService'
import { apolloClient } from './apolloClient'
import { ConnectionResult } from './listQuery'

export const adminUsersQuery = gql`
  query AdminUsers(
    $first: Int
    $skip: Int
    $filter: AdminUserFilterInput
    $orderBy: [AdminUserOrderByInput!]
  ) {
    AdminUser_list(
      first: $first
      skip: $skip
      filter: $filter
      orderBy: $orderBy
    ) {
      totalCount
      hasNextPage
      hasPreviousPage
      items {
        id
        email
        name
        nickname
        roles
        createdDate
        updatedDate
        organizations {
          id
          name
          displayName
        }
      }
    }
  }
`

export const adminUserByIdQuery = gql`
  query AdminUserById($id: String!) {
    adminUserById(id: $id) {
      id
      email
      name
      nickname
      roles
      createdDate
      updatedDate
      organizations {
        id
        name
        displayName
      }
    }
  }
`

export type AdminUser = {
  id: string
  email: string
  name?: string | null
  nickname?: string | null
  roles?: any
  createdDate: string
  updatedDate: string
  organizations?: Pick<AdminOrganization, 'id' | 'name' | 'displayName'>[]
}

export type AdminUserFilter = {
  email?: { contains?: string }
  name?: { contains?: string }
  nickname?: { contains?: string }
  organizations?: {
    some?: {
      name?: { contains?: string }
      displayName?: { contains?: string }
    }
  }
}

export type AdminUserOrderByInput = {
  email?: 'ASC' | 'DESC'
  name?: 'ASC' | 'DESC'
  nickname?: 'ASC' | 'DESC'
  createdDate?: 'ASC' | 'DESC'
  updatedDate?: 'ASC' | 'DESC'
}

export type AdminUsersQueryOptions = {
  first?: number
  skip?: number
  filter?: AdminUserFilter
  orderBy?: AdminUserOrderByInput[]
}

export const mapAdminUsersSortingToOrderBy = (
  sorting: SortingState,
): AdminUserOrderByInput[] | undefined => {
  const orderBy: AdminUserOrderByInput[] = []

  sorting.forEach(sort => {
    const direction = sort.desc ? 'DESC' : 'ASC'

    if (sort.id === 'email') orderBy.push({ email: direction })
    if (sort.id === 'name') orderBy.push({ name: direction })
    if (sort.id === 'nickname') orderBy.push({ nickname: direction })
    if (sort.id === 'createdDate') orderBy.push({ createdDate: direction })
    if (sort.id === 'updatedDate') orderBy.push({ updatedDate: direction })
  })

  return orderBy.length > 0 ? orderBy : undefined
}

export const getAdminUsers = async (options?: {
  first?: number
  skip?: number
  filter?: AdminUserFilter
  orderBy?: AdminUserOrderByInput[]
}): Promise<ConnectionResult<AdminUser>> => {
  const result = await apolloClient.query({
    query: adminUsersQuery,
    variables: {
      first: options?.first ?? 50,
      skip: options?.skip ?? 0,
      filter: options?.filter,
      orderBy: options?.orderBy,
    },
  })
  return ((result.data as any)?.AdminUser_list ?? {
    items: [],
  }) as ConnectionResult<AdminUser>
}

export const getAdminUserById = async (
  id: string,
): Promise<AdminUser | null> => {
  const result = await apolloClient.query({
    query: adminUserByIdQuery,
    variables: { id },
  })
  return ((result.data as any)?.adminUserById ?? null) as AdminUser | null
}
